import {StyleSheet} from "aphrodite";
import {useId, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import {ModalLauncher, OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {doNotTranslateYet} from "@khan/i18n";

import CriteriaList from "./criteria-list.tsx";
import * as strings from "./strings.tsx";
import {checkFilenameCriteria, pythonFileExtension} from "./utils.ts";

type Props = {
    // The name of the file being renamed, if applicable.
    currentFilename?: string;
    // The set of filenames in the editor.
    filenames: Set<string>;
    // Called when the filename entry is cancelled.
    onCancel: () => void;
    // Called when a filename is confirmed.
    onSubmit: (newName: string) => void;
};

export default function FilenameEntryModal({
    currentFilename,
    filenames,
    onCancel,
    onSubmit,
}: Props): React.ReactElement {
    const inputId = useId();

    const [editedFilenameWithoutExtension, setEditedFilenameWithoutExtension] =
        useState(
            currentFilename?.substring(
                0,
                currentFilename.length - pythonFileExtension.length,
            ) ?? "",
        );
    const editedFilename = `${editedFilenameWithoutExtension}${pythonFileExtension}`;

    const filenameCriteria = checkFilenameCriteria(
        editedFilenameWithoutExtension,
        currentFilename,
        filenames,
    );
    const isValidFilename = filenameCriteria.every((c) => c.isSatisfied);

    return (
        <ModalLauncher
            opened={true}
            onClose={onCancel}
            modal={
                <OnePaneDialog
                    style={styles.dialog}
                    title={
                        currentFilename == null
                            ? doNotTranslateYet`Create file`
                            : strings.renameFile(currentFilename)
                    }
                    content={
                        <View
                            onKeyUp={(e) => {
                                if (e.key === "Enter" && isValidFilename) {
                                    onSubmit(editedFilename);
                                }
                            }}
                        >
                            <label className="sr-only" htmlFor={inputId}>
                                {currentFilename == null
                                    ? doNotTranslateYet`Name of new file`
                                    : strings.renameFileInputLabel(
                                          currentFilename,
                                      )}
                            </label>
                            <View style={styles.inputWrapper}>
                                <TextField
                                    id={inputId}
                                    value={editedFilenameWithoutExtension}
                                    onChange={setEditedFilenameWithoutExtension}
                                />
                                <LabelMedium>{pythonFileExtension}</LabelMedium>
                            </View>
                            <CriteriaList criteria={filenameCriteria} />
                        </View>
                    }
                    footer={
                        <View style={styles.footer}>
                            <Button kind="tertiary" onClick={onCancel}>
                                {doNotTranslateYet`Cancel`}
                            </Button>
                            <Button
                                kind="primary"
                                disabled={!isValidFilename}
                                onClick={() => onSubmit(editedFilename)}
                            >
                                {doNotTranslateYet`Save`}
                            </Button>
                        </View>
                    }
                />
            }
        />
    );
}

const styles = StyleSheet.create({
    dialog: {
        maxHeight: 530,
    },
    footer: {
        flexBasis: "100%",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: spacing.medium_16,
    },
    inputWrapper: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xxSmall_6,
    },
});
